/**
 * Shared config widgets for a field's "choices" and "directory filter" —
 * used from both the field library's own edit modal (FieldLibraryPage) and
 * the embedded config panel inside the form builder (FormLayoutEditor).
 * Pulled out into one file so the two surfaces can't quietly drift into two
 * different option-editing experiences again (Kai: "there's 4 settings
 * pages to do what 1 should do").
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Archive, ArchiveRestore, ChevronDown, ChevronUp, Plus, X } from 'lucide-react';
import type { Condition, FieldType, SelectOption } from '@itsm/shared';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { apiFetch } from '../../lib/api';
import { qk } from '../../lib/queryClient';

export const TYPE_LABELS: Record<FieldType, string> = {
  text: 'Single-line text',
  textarea: 'Paragraph',
  richtext: 'Rich text',
  number: 'Number',
  decimal: 'Decimal',
  select: 'Dropdown',
  multiselect: 'Multi-select',
  radio: 'Radio group',
  checkbox: 'Checkbox',
  date: 'Date',
  datetime: 'Date & time',
  url: 'URL',
  email: 'Email',
  phone: 'Phone',
  file: 'File attachment',
  person: 'Person lookup',
  text_list: 'Repeatable text list',
  content: 'Content block (no input)',
};

export function slugify(s: string): string {
  return (
    s
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '') || 'value'
  );
}

/** A sortable list of choice rows, matching the reference tool's own
 *  "Dropdown Choices" editor (drag/reorder + delete per row, an "add
 *  choice" affordance, and a bulk-paste toggle) — re-themed here rather
 *  than the free-text `value|Label` blob it replaces (Kai: "I really hate
 *  Executives|Executives..."). A plain custom-dropdown choice is ONE
 *  string; `value` is derived from it and never shown. */
export function OptionsEditor({
  options,
  onChange,
}: {
  options: SelectOption[];
  onChange: (o: SelectOption[]) => void;
}) {
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkText, setBulkText] = useState('');

  function updateLabel(i: number, label: string) {
    const row = options[i]!;
    // Re-slug only while the value still matches this row's OWN prior
    // auto-derived value — an imported/curated value code (e.g. "fm",
    // "global_player_app") must never be silently rewritten underneath it.
    const nextValue = row.value === '' || row.value === slugify(row.label) ? slugify(label) : row.value;
    onChange(options.map((o, idx) => (idx === i ? { ...o, label, value: nextValue } : o)));
  }
  function move(i: number, dir: -1 | 1) {
    const j = i + dir;
    if (j < 0 || j >= options.length) return;
    const next = [...options];
    [next[i], next[j]] = [next[j]!, next[i]!];
    onChange(next);
  }

  if (bulkMode) {
    return (
      <div className="space-y-1.5">
        <p className="text-[11px] text-muted-foreground">Type in each choice on its own line.</p>
        <Textarea
          rows={5}
          value={bulkText}
          onChange={(e) => setBulkText(e.target.value)}
          placeholder={'Executives\nTechnology & Digital\nFinance'}
        />
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={() => setBulkMode(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={() => {
              const typed = bulkText
                .split('\n')
                .map((l) => l.trim())
                .filter(Boolean)
                .map((l) => ({ value: slugify(l), label: l }));
              // Archived choices are NOT in the bulk box (they are history,
              // not an editable list) — carry them through, or a bulk edit
              // would silently orphan every ticket holding one.
              const keptArchived = options.filter(
                (o) => o.archived && !typed.some((t) => t.value === o.value),
              );
              onChange([...typed, ...keptArchived]);
              setBulkMode(false);
            }}
          >
            Apply
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between pb-0.5">
        <span className="text-[11px] text-muted-foreground">
          {options.filter((o) => !o.archived).length} choice
          {options.filter((o) => !o.archived).length === 1 ? '' : 's'}
          {options.some((o) => o.archived) && ` · ${options.filter((o) => o.archived).length} archived`}
        </span>
        <button
          type="button"
          onClick={() => {
            setBulkText(
              options
                .filter((o) => !o.archived)
                .map((o) => o.label)
                .join('\n'),
            );
            setBulkMode(true);
          }}
          className="text-xs text-brand hover:underline"
        >
          Add/edit in bulk
        </button>
      </div>
      {options.map((o, i) => (
        <div key={i} className="flex items-center gap-1">
          <div className="flex shrink-0 flex-col">
            <button
              type="button"
              onClick={() => move(i, -1)}
              disabled={i === 0 || !!o.archived}
              className="text-muted-foreground hover:text-foreground disabled:opacity-25"
            >
              <ChevronUp className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={() => move(i, 1)}
              disabled={i === options.length - 1 || !!o.archived}
              className="text-muted-foreground hover:text-foreground disabled:opacity-25"
            >
              <ChevronDown className="h-3 w-3" />
            </button>
          </div>
          <Input
            value={o.label}
            onChange={(e) => updateLabel(i, e.target.value)}
            placeholder="Choice"
            disabled={!!o.archived}
            className="h-8 flex-1 text-xs"
          />
          {o.archived && <span className="shrink-0 text-[10px] text-muted-foreground">archived</span>}
          {/* ARCHIVE, never delete (Step 67): a removed choice orphans the
              value on every ticket that already picked it. A choice nobody
              could have picked yet (blank label) is still safe to drop. */}
          {o.archived ? (
            <button
              type="button"
              onClick={() => onChange(options.map((x, idx) => (idx === i ? { ...x, archived: false } : x)))}
              className="shrink-0 text-muted-foreground hover:text-foreground"
              aria-label={`Restore choice ${o.label}`}
              title="Restore this choice"
            >
              <ArchiveRestore className="h-3.5 w-3.5" />
            </button>
          ) : o.label.trim() === '' ? (
            <button
              type="button"
              onClick={() => onChange(options.filter((_, idx) => idx !== i))}
              className="shrink-0 text-muted-foreground hover:text-destructive"
              aria-label="Remove empty choice"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => onChange(options.map((x, idx) => (idx === i ? { ...x, archived: true } : x)))}
              className="shrink-0 text-muted-foreground hover:text-foreground"
              aria-label={`Archive choice ${o.label}`}
              title="Archive — hidden from new submissions, still shown on tickets that hold it"
            >
              <Archive className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...options, { value: '', label: '' }])}
        className="flex items-center gap-1 pt-1 text-xs text-brand hover:underline"
      >
        <Plus className="h-3.5 w-3.5" /> Add choice
      </button>
    </div>
  );
}

type PeopleAttr = 'kind' | 'jobTitle' | 'department' | 'employeeId' | 'officeLocation' | 'group';
const PEOPLE_ATTRS: { value: PeopleAttr; label: string }[] = [
  { value: 'kind', label: 'Type' },
  { value: 'jobTitle', label: 'Job title' },
  { value: 'department', label: 'Department' },
  { value: 'employeeId', label: 'Employee ID' },
  { value: 'officeLocation', label: 'Office location' },
  { value: 'group', label: 'Group membership' },
];
type PeopleOp = 'eq' | 'neq' | 'contains' | 'notContains' | 'isEmpty' | 'isNotEmpty';
const OPS_FOR: Record<PeopleAttr, { value: PeopleOp; label: string }[]> = {
  kind: [
    { value: 'eq', label: 'is' },
    { value: 'neq', label: 'is not' },
  ],
  group: [
    { value: 'eq', label: 'is a member of' },
    { value: 'neq', label: 'is not a member of' },
  ],
  jobTitle: [
    { value: 'contains', label: 'contains' },
    { value: 'notContains', label: 'does not contain' },
    { value: 'eq', label: 'is exactly' },
    { value: 'isEmpty', label: 'is empty' },
    { value: 'isNotEmpty', label: 'is not empty' },
  ],
  department: [
    { value: 'eq', label: 'is' },
    { value: 'neq', label: 'is not' },
    { value: 'contains', label: 'contains' },
    { value: 'isEmpty', label: 'is empty' },
    { value: 'isNotEmpty', label: 'is not empty' },
  ],
  employeeId: [
    { value: 'eq', label: 'is' },
    { value: 'isEmpty', label: 'is empty' },
    { value: 'isNotEmpty', label: 'is not empty' },
  ],
  officeLocation: [
    { value: 'eq', label: 'is' },
    { value: 'contains', label: 'contains' },
    { value: 'isEmpty', label: 'is empty' },
    { value: 'isNotEmpty', label: 'is not empty' },
  ],
};
interface PeopleFilterRow {
  attr: PeopleAttr;
  op: PeopleOp;
  value: string;
}
function rowToCondition(r: PeopleFilterRow): Condition {
  const fieldKey = r.attr === 'group' ? `group:${r.value}` : r.attr;
  if (r.attr === 'group') return { op: r.op === 'neq' ? 'neq' : 'eq', fieldKey, value: true };
  if (r.op === 'isEmpty' || r.op === 'isNotEmpty') return { op: r.op, fieldKey };
  if (r.op === 'contains' || r.op === 'notContains') return { op: r.op, fieldKey, value: r.value };
  return { op: r.op, fieldKey, value: r.value };
}

/** Mirrors the reference tool's "Select from Data Source" alternative to a
 *  custom dropdown — options come from matching the directory instead of a
 *  hand-typed list (Kai: "populate the dropdowns with people's names who
 *  have X job title... with a good way of doing anyone with X and X but not
 *  X or includes Y"). Reuses the SAME Condition grammar as showWhen. Only
 *  round-trips a filter this editor itself produced (a flat and/or of
 *  simple rows) — anything more exotic shows as a read-only summary. */
export function PeopleFilterEditor({
  filter,
  onChange,
}: {
  filter: Condition | undefined;
  onChange: (c: Condition | undefined) => void;
}) {
  const { data: groupsData } = useQuery({
    queryKey: qk.adminGroups,
    queryFn: () => apiFetch<{ entries: { id: string; name: string }[] }>('/api/admin/groups'),
  });
  const groups = groupsData?.entries ?? [];

  const flat = filter && (filter.op === 'and' || filter.op === 'or') ? filter : undefined;
  if (filter && !flat) {
    return (
      <div className="space-y-2 rounded-md border bg-muted/20 p-2.5 text-xs text-muted-foreground">
        <p>A custom filter is set that this editor can't display row-by-row.</p>
        <Button size="sm" variant="outline" onClick={() => onChange({ op: 'and', conditions: [] })}>
          Replace with a new filter
        </Button>
      </div>
    );
  }
  const matchMode: 'and' | 'or' = flat?.op ?? 'and';
  const rows: PeopleFilterRow[] = (flat?.conditions ?? []).map((c: Condition) => {
    if ('fieldKey' in c && c.fieldKey.startsWith('group:')) {
      return { attr: 'group', op: c.op === 'neq' ? 'neq' : 'eq', value: c.fieldKey.slice('group:'.length) };
    }
    if (c.op === 'isEmpty' || c.op === 'isNotEmpty')
      return { attr: c.fieldKey as PeopleAttr, op: c.op, value: '' };
    if ('value' in c) return { attr: c.fieldKey as PeopleAttr, op: c.op as PeopleOp, value: String(c.value) };
    return { attr: 'jobTitle', op: 'contains', value: '' };
  });

  function setRows(next: PeopleFilterRow[]) {
    onChange({ op: matchMode, conditions: next.map(rowToCondition) });
  }
  function setMatchMode(mode: 'and' | 'or') {
    onChange({ op: mode, conditions: rows.map(rowToCondition) });
  }
  function updateRow(i: number, patch: Partial<PeopleFilterRow>) {
    setRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }

  return (
    <div className="space-y-1.5 rounded-md border bg-muted/10 p-2.5">
      <div className="flex items-center gap-1.5 text-xs">
        <span className="text-muted-foreground">Match</span>
        <Select
          value={matchMode}
          onChange={(e) => setMatchMode(e.target.value as 'and' | 'or')}
          className="h-7 w-20 text-xs"
        >
          <option value="and">ALL</option>
          <option value="or">ANY</option>
        </Select>
        <span className="text-muted-foreground">of these:</span>
      </div>
      {rows.map((r, i) => (
        <div key={i} className="flex flex-wrap items-center gap-1.5">
          <Select
            value={r.attr}
            onChange={(e) => {
              const attr = e.target.value as PeopleAttr;
              updateRow(i, { attr, op: OPS_FOR[attr][0]!.value, value: '' });
            }}
            className="h-7 w-32 text-xs"
          >
            {PEOPLE_ATTRS.map((a) => (
              <option key={a.value} value={a.value}>
                {a.label}
              </option>
            ))}
          </Select>
          <Select
            value={r.op}
            onChange={(e) => updateRow(i, { op: e.target.value as PeopleOp })}
            className="h-7 w-32 text-xs"
          >
            {OPS_FOR[r.attr].map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
          {r.op !== 'isEmpty' && r.op !== 'isNotEmpty' && (
            <>
              {r.attr === 'kind' ? (
                <Select
                  value={r.value}
                  onChange={(e) => updateRow(i, { value: e.target.value })}
                  className="h-7 w-28 text-xs"
                >
                  <option value="">…</option>
                  <option value="agent">Agent</option>
                  <option value="requester">Requester</option>
                  <option value="platform">Platform</option>
                </Select>
              ) : r.attr === 'group' ? (
                <Select
                  value={r.value}
                  onChange={(e) => updateRow(i, { value: e.target.value })}
                  className="h-7 w-32 text-xs"
                >
                  <option value="">…</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </Select>
              ) : (
                <Input
                  value={r.value}
                  onChange={(e) => updateRow(i, { value: e.target.value })}
                  className="h-7 w-32 text-xs"
                  placeholder="Value"
                />
              )}
            </>
          )}
          <button
            type="button"
            onClick={() => setRows(rows.filter((_, idx) => idx !== i))}
            className="text-muted-foreground hover:text-destructive"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => setRows([...rows, { attr: 'jobTitle', op: 'contains', value: '' }])}
        className="flex items-center gap-1 pt-0.5 text-xs text-brand hover:underline"
      >
        <Plus className="h-3.5 w-3.5" /> Add condition
      </button>
    </div>
  );
}
